// tslint:disable no-bitwise
declare const Human: any;
declare const vec3: any;

import { Injectable } from '@angular/core';
import { XYZModel } from '../shared/models/xyz.model';
import { HitModel } from './models/hit.model';
import { CameraModel } from './models/camera.model';
import { NumberService } from '../shared/number.service';

const epsilon = 0.001;
const zoomRate = 0.03;
const cameraFriction = 0.85;


@Injectable({
  providedIn: 'root'
})
export class CameraService {
  rotation = { vx: 0, vy: 0 };
  pan = { x: 0, y: 0 };
  zoom = 0;

  constructor(private numberService: NumberService) {

  }
  
  getEye(): XYZModel {
    return Human.renderer.camera.eye as XYZModel;
  }

  setEye(eye: XYZModel) {
    Human.renderer.camera.setLookAt({
      eye
    });
  }

  getCamera(): CameraModel {
    return Human.renderer.camera.getLookAt() as CameraModel;
  }

  setCamera(camera: CameraModel) {
    return Human.renderer.camera.setLookAt(camera);
  }

  flyTo(camera: CameraModel) {
    return Human.renderer.camera.flyTo(camera);
  }
  
  getDirection(hit: HitModel) {
    const camera = this.getEye();
    const direction = vec3.create();
    direction[0] = camera.x - hit.worldPos[0];
    direction[1] = camera.y - hit.worldPos[1];
    direction[2] = camera.z - hit.worldPos[2];
    vec3.normalize(direction, direction);

    return direction;
  }

  setCameraRotation(velocityX: number, velocityY: number) {
    this.rotation.vx = velocityY;
    this.rotation.vy = -velocityX;
  }
  
  setCameraPan(velocityX: number, velocityY: number) {
    this.pan.x = velocityX;
    this.pan.y = velocityY;
  }

  setCameraZoom(deltaY: number) {
    this.zoom = deltaY * zoomRate;
  }
  
  tickCameraRotation() {
    this.rotation.vx *= cameraFriction;
    this.rotation.vy *= cameraFriction;

    this.rotation.vx = this.checkEpsilon(this.rotation.vx);
    this.rotation.vy = this.checkEpsilon(this.rotation.vy);
    
    return this.rotation;
  }
  
  tickCameraPan() {
    this.pan.x *= cameraFriction;
    this.pan.y *= cameraFriction;
    
    this.pan.x = this.checkEpsilon(this.pan.x);
    this.pan.y = this.checkEpsilon(this.pan.y);
    
    return this.pan;
  }

  tickCameraZoom() {
    this.zoom *= cameraFriction;
    this.zoom = this.checkEpsilon(this.zoom);

    return this.zoom;
  }

  /**
   * Returns if the cameras are equivalent.
   */
  compareCameras(camera1: CameraModel, camera2: CameraModel): boolean {
    return this.compareXYZ(camera1.eye, camera2.eye)
      && this.compareXYZ(camera1.look, camera2.look)
      && this.compareXYZ(camera1.up, camera2.up);
  }

  /**
   * Returns if the XYZ coordinates are equal.
   */
  compareXYZ(model1: XYZModel, model2: XYZModel): boolean {
    return model1.x === model2.x
      && model1.y === model2.y
      && model1.z === model2.z;
  }

  /**
   * Rounds the camera to the specified precision.
   */
  roundCamera(camera: CameraModel, precision: number): CameraModel {
    const { eye, look, up } = camera;
    return {
      eye: this.numberService.roundModel<XYZModel>(eye, precision),
      look: this.numberService.roundModel<XYZModel>(look, precision),
      up: this.numberService.roundModel<XYZModel>(up, precision)
    };
  }

  private checkEpsilon(value: number) {
    return (Math.abs(value) < epsilon) ? 0 : value;
  }
}
